/**
 * Category tab definitions for the inventory count-cards row. Each card maps a
 * display label + icon to a `value` that resolves — via `CATEGORY_MAP` — to one
 * or more DB `category` keys. The card count sums `equipment_by_category` +
 * `components_by_category` from the dashboard stats over the resolved keys, and
 * selecting a card pushes the RESOLVED category to the `/items` list request.
 */
import {
  Server,
  Cpu,
  MemoryStick,
  HardDrive,
  Network,
  AppWindow,
  Laptop,
  Box,
  type LucideIcon,
} from 'lucide-react';
import type { DashboardStats } from '@/api/capacities';

export interface CategoryTab {
  label: string;
  value: string;
  Icon: LucideIcon;
  /** Icon accent color (token var). */
  color: string;
}

/** One row of selectable category cards, in legacy order. */
export const CATEGORY_TABS: CategoryTab[] = [
  { label: 'Server', value: 'server', Icon: Server, color: 'var(--vn-accent-solid)' },
  { label: 'Processor', value: 'cpu', Icon: Cpu, color: 'var(--vn-success-solid)' },
  { label: 'Memory', value: 'ram', Icon: MemoryStick, color: 'var(--vn-info-solid)' },
  { label: 'Disk', value: 'disk', Icon: HardDrive, color: 'var(--vn-warning-solid)' },
  { label: 'GPU', value: 'card_gpu', Icon: Box, color: 'var(--vn-danger-solid)' },
  { label: 'Network Card', value: 'card_network', Icon: Network, color: 'var(--vn-info-solid)' },
  { label: 'Switch', value: 'switch', Icon: AppWindow, color: 'var(--vn-primary-solid)' },
  { label: 'Laptop', value: 'laptop', Icon: Laptop, color: 'var(--vn-success-solid)' },
  { label: 'Module', value: 'module', Icon: Box, color: 'var(--vn-danger-solid)' },
];

/**
 * Tab value → actual DB category value(s). Disk is a grouped bucket covering the
 * distinct storage media categories; every other tab maps 1:1 to its own key.
 */
export const CATEGORY_MAP: Record<string, string> = {
  disk: 'sas,ssd,nvme,hdd',
};

/** Resolve a tab value to the comma-joined DB category list sent to `/items`. */
export const resolveCategory = (tab: string): string => CATEGORY_MAP[tab] ?? tab;

/** Merge equipment + component per-category counts into one lookup map. */
export function buildCategoryCountMap(stats?: DashboardStats): Record<string, number> {
  const map: Record<string, number> = {};
  if (!stats) return map;
  for (const [k, v] of Object.entries(stats.equipment_by_category ?? {})) {
    map[k] = (map[k] ?? 0) + v;
  }
  for (const [k, v] of Object.entries(stats.components_by_category ?? {})) {
    map[k] = (map[k] ?? 0) + v;
  }
  return map;
}

/** Sum the counts of all DB categories a tab resolves to. */
export function categoryCount(tab: string, countMap: Record<string, number>): number {
  return resolveCategory(tab)
    .split(',')
    .reduce((sum, key) => sum + (countMap[key] ?? 0), 0);
}
